import React, { useRef, useState } from 'react'
import { Transforms } from 'slate'
import { ReactEditor } from 'slate-react'
import { Button, ButtonGroup, Icon, Popover, TextField, cn, hasVisibleOnHoverChild, makeStyles } from '@cicero/ui'
import { IImageElement } from '../commands'
import { EditorService, IEditorElementProps, IElementDefinition } from '../EditorService'
import { ElementBlock } from './ParagraphElement'

export const ImageElement = ({ children, attributes, element }: IEditorElementProps<IImageElement>) => {
  const service = EditorService.useEditor()
  const selected = EditorService.useSelected()
  const focused = EditorService.useFocused()

  return <ElementBlock attributes={attributes}>
    <Image
      url={element.url}
      setUrl={(url) => {
        const path = ReactEditor.findPath(service.editor, element)
        Transforms.setNodes(service.editor, { url }, { at: path })
      }}
      focused={selected && focused}
    />
    {children}
  </ElementBlock>
}

export const Image = ({ url, setUrl, focused }: { url?: string, setUrl: ((url: string|null) => void), focused?: boolean }) => {
  const { image, imageWrapper, imageHoverButtons, imageFocused, emptyImage } = useImageStyles()
  const [editing, setEditing] = useState(false)
  const [currentUrl, setCurrentUrl] = useState('')
  const usingEl = useRef<HTMLSpanElement>(null)

  const startEditing = () => {
    setEditing(true)
    setCurrentUrl(url || '')
  }

  return <span contentEditable={false} className={cn(imageWrapper, { [imageFocused]: focused })} ref={usingEl}>
    <ButtonGroup color="primary" className={cn(imageHoverButtons, { 'visible-on-hover': !focused })}>
      <Button onClick={() => startEditing()}>Edit</Button>
      { url ? <Button onClick={() => setUrl(null)}>Clear</Button> : null }
    </ButtonGroup>
    {
      url
        ? <img src={url} className={image} />
        : <div className={emptyImage} onClick={() => startEditing()}>
          <Icon>image</Icon>
        </div>
    }
    <Popover
      open={editing}
      anchorEl={usingEl.current}
      onClose={() => setEditing(false)}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'center',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'center',
      }}
    >
      <form onSubmit={(e) => {
        e.preventDefault()
        setUrl(currentUrl || null)
      }}>
        <TextField
          autoFocus
          placeholder="Add Image URL"
          value={currentUrl}
          onChange={(ev: any) => setCurrentUrl(ev.target.value)}
        />
      </form>
    </Popover>
  </span>
}

export const ImageElementDef: IElementDefinition = {
  renderer(props) { return <ImageElement {...props} /> },
  setup(service) {
    const { isVoid } = service.editor
    service.editor.isVoid = (element) => element.type === 'image' ? true : isVoid(element)
  },
}

const useImageStyles = makeStyles({
  imageWrapper: {
    position: 'relative',
    display: 'inline-block',
    ...hasVisibleOnHoverChild,
  },
  imageHoverButtons: {
    position: 'absolute',
    top: 2,
    right: 2,
  },
  image: {
    display: 'block',
    maxWidth: '100%',
    minHeight: 160,
    minWidth: 200,
    maxHeight: '20em',
  },
  imageFocused: {
    boxShadow: '0 0 0 3px #B4D5FF',
  },
  emptyImage: {
    alignItems: 'center',
    cursor: 'pointer',
    background: '#EEE',
    display: 'flex',
    justifyContent: 'center',
    minHeight: 260,
    minWidth: 400,
    width: '100%',
    '&:hover': {
      background: '#DDD',
    },
  },
})
